/**
 * InlineEditWrapper Component (T101)
 * Higher Order Component that adds hover-to-edit functionality
 * 
 * Features:
 * - Shows pen icon on hover
 * - Click pen icon to enter edit mode
 * - Switches between display and edit components
 * - Manages edit state using useInlineEdit hook
 * - Supports disabled state
 * - Keyboard accessible
 * 
 * @example
 * <InlineEditWrapper
 *   value={provider.companyName}
 *   onSave={(field, value) => updateProvider({ [field]: value })}
 *   fieldName="companyName"
 *   displayComponent={({ value }) => <span>{value}</span>}
 *   editComponent={InlineTextField}
 * />
 */

import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

type InlineEditWrapperProps<T = string> = {
  /** Current field value */
  value: T;
  
  /** Callback when value is saved - receives field name and new value */
  onSave: (fieldName: string, value: T) => Promise<void> | void;
  
  /** Field name for save callback */
  fieldName: string;
  
  /** Component to display value (display mode) */
  displayComponent: React.ComponentType<{ value: T }>;
  
  /** Component to edit value (edit mode) */
  editComponent: React.ComponentType<{
    value: T;
    onChange: (value: T) => void;
    onSave: () => void;
    onCancel: () => void;
  }>;
  
  /** Whether editing is disabled */
  disabled?: boolean;
  
  /** Optional CSS class for wrapper */
  className?: string;
};

/**
 * InlineEditWrapper - HOC for hover-to-edit functionality
 * 
 * Wraps display/edit components with:
 * - Hover state detection
 * - Pen icon on hover
 * - Mode switching (display/edit)
 * - Save/cancel operations
 * 
 * Note: This component manages UI state only.
 * The edit component is responsible for keyboard shortcuts (Enter/Escape).
 */
export function InlineEditWrapper<T = string>({
  value,
  onSave,
  fieldName,
  displayComponent: DisplayComponent,
  editComponent: EditComponent,
  disabled = false,
  className,
}: InlineEditWrapperProps<T>) {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);

  // Enter edit mode
  const handleStartEdit = () => {
    if (!disabled) {
      setIsEditing(true);
      setCurrentValue(value); // Reset to current value
    }
  };

  // Save changes
  const handleSave = async () => {
    try {
      await onSave(fieldName, currentValue);
      setIsEditing(false);
    } catch (error) {
      // Remain in edit mode on error (allow retry)
      console.error('Inline edit save failed:', error);
    }
  };

  // Cancel changes
  const handleCancel = () => {
    setCurrentValue(value); // Revert to original value
    setIsEditing(false);
  };

  // Update current value during editing
  const handleChange = (newValue: T) => {
    setCurrentValue(newValue);
  };

  // If in edit mode, show edit component
  if (isEditing) {
    return (
      <div className={cn('inline-edit-wrapper--editing', className)}>
        <EditComponent
          value={currentValue}
          onChange={handleChange}
          onSave={handleSave}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  // Display mode - show value with hover pen icon
  return (
    <div
      className={cn(
        'group relative inline-block',
        !disabled && 'hover:bg-gray-50 rounded px-1 -mx-1 transition-colors',
        className
      )}
      onMouseEnter={() => !disabled && setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* Display Component */}
      <DisplayComponent value={value} />

      {/* Pen Icon (visible on hover) */}
      {isHovering && !disabled && (
        <button
          type="button"
          onClick={handleStartEdit}
          className="absolute -right-6 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#026857] focus:ring-offset-1"
          aria-label={`Edit ${fieldName}`}
        >
          <Pencil className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

